// «Clase suelta» desde la Caja (Karla 30-sep-2026, videos 15:17–15:23): la persona llega al mostrador,
// paga la clase y entra. Recepción captura SOLO nombre, teléfono y clase; el cobro sale al instante y la
// persona queda como PROSPECTO (no alumna: `prospecto: true` en POST /members → rol lead).
// Teléfono o nombre repetido → el 409 de antiduplicados enseña quién es (alumna / ex-alumna / prospecto
// / baja) y recepción elige «Es esta persona» o «Es otra persona» (mamá e hijas comparten número).
// Precio: trial-check (precio propio de la clase o $120 L-V / $220 sábado; muestra gratis si aplica).
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Ticket, UserCheck, UserPlus, AlertTriangle } from 'lucide-react';
import { createMember, trialCheck, TrialCheck, DuplicateMatch } from '../../members/api';
import { getCalendar, CalBlock, isActiveOn, ymd, hhmm } from '../../classes/api';
import { createPayment, PAY_METHOD_OPTIONS } from '../api';
import DolaresMostrador, { METODO_DOLARES, useDolares, registrarDolares } from './DolaresMostrador';
import { ApiError } from '../../../services/apiClient';
import { useToast } from '../../../context/ToastContext';

type Dup = DuplicateMatch;
const TIPO: Record<string, { label: string; cls: string }> = {
  alumna: { label: 'Alumna', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  ex_alumna: { label: 'Ex-alumna', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  visitante: { label: 'Ya vino a clase suelta', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  prospecto: { label: 'Prospecto', cls: 'bg-surface-2 text-content-3' },
  baja: { label: 'Dada de baja', cls: 'bg-surface-2 text-content-3' },
};
const money = (n: number) => `$${Number(n || 0).toLocaleString('es-MX')}`;
const inputCls = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

interface ClaseOpt { class_id: number; name: string; hora: string | null; hoy: boolean }

// Desde la ficha de la clase (F3): «Añadir alumno…» encuentra a un prospecto y abre este mismo modal con
// la persona y la clase ya puestas — cobrar y que salga en la lista de hoy, sin inscribirlo.
export default function ClaseSueltaModal({ cajaAbierta, onClose, onDone, personaInicial = null, claseInicial = null }: {
  cajaAbierta: boolean; onClose: () => void; onDone: () => void;
  personaInicial?: { id: number; name: string } | null; claseInicial?: number | null;
}) {
  const toast = useToast();
  const hoy = ymd(new Date());
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [tel, setTel] = useState('');
  const [blocks, setBlocks] = useState<CalBlock[]>([]);
  const [claseId, setClaseId] = useState<number | null>(claseInicial);
  const [method, setMethod] = useState('cash');
  const dol = useDolares();   // «Dólares» (Mabel 10-oct-2026): la clase va en pesos y los USD entran a la caja
  const [persona, setPersona] = useState<{ id: number; name: string } | null>(personaInicial);   // ficha existente elegida
  const [dups, setDups] = useState<Dup[] | null>(null);
  const [check, setCheck] = useState<TrialCheck | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getCalendar().then((r) => setBlocks(r.data)).catch(() => {}); }, []);

  // Clases: primero las que hay HOY (por hora), luego el resto vigente.
  const clases = useMemo<ClaseOpt[]>(() => {
    const wd = new Date().getDay();
    const by = new Map<number, ClaseOpt>();
    for (const b of blocks) {
      if (!isActiveOn(b, hoy) && !(b.start_date && b.start_date > hoy)) continue;   // ciclo terminado
      const esHoy = b.weekday === wd && isActiveOn(b, hoy);
      const prev = by.get(b.class_id);
      if (!prev || (esHoy && !prev.hoy)) by.set(b.class_id, { class_id: b.class_id, name: b.name, hora: esHoy ? hhmm(b.start_time) : null, hoy: esHoy });
    }
    return [...by.values()].sort((a, b) => (a.hoy === b.hoy ? (a.hora || a.name).localeCompare(b.hora || b.name) : a.hoy ? -1 : 1));
  }, [blocks, hoy]);

  // Precio: con la clase (y la persona, si es alguien que ya existe: una alumna puede tener muestra gratis).
  useEffect(() => {
    if (!claseId) { setCheck(null); return; }
    let cancel = false;
    trialCheck({ class_id: claseId, contact_id: persona?.id ?? null, date: hoy })
      .then((c) => { if (!cancel) setCheck(c); }).catch(() => { if (!cancel) setCheck(null); });
    return () => { cancel = true; };
  }, [claseId, persona?.id, hoy]);

  const telDigits = tel.replace(/\D/g, '');
  const listo = (persona || (nombre.trim() && telDigits.length >= 10)) && claseId && check;

  async function cobrar(contactId: number, quien: string) {
    if (!check) return;
    const gratis = check.free_available;
    const enUsd = method === METODO_DOLARES && !gratis;
    if (enUsd && !(dol.usdNum > 0)) { toast.error('Indica cuántos dólares te dio'); return; }
    await createPayment({
      account_id: contactId,
      type: gratis ? 'voucher_trial' : 'class',
      method: method === METODO_DOLARES ? 'cash' : method, amount: gratis ? 0 : check.charge, pay_date: hoy,
      assoc_name: check.class_name || null, comment: null, months: null,
      class_id: claseId, class_date: hoy,   // → sale en «Pasar lista» de esa clase hoy, sin inscribirla
      is_partial: false, total_expected: null, debt_id: null, discount_id: null,
    });
    const avisoUsd = enUsd ? await registrarDolares(dol.usdNum, dol.tc, `Clase suelta ${money(check.charge)} · ${quien}`, dol.setTc) : null;
    if (avisoUsd) toast.error(avisoUsd);
    toast.success(gratis ? `Clase muestra GRATIS registrada · ${quien}` : `Clase suelta cobrada (${money(check.charge)}${enUsd ? ` · ${dol.usdNum} USD` : ''}) · ${quien}`);
    onDone();
  }

  // force = «Es otra persona» tras ver las coincidencias.
  async function submit(force = false) {
    if (!listo || saving) return;
    // Antes de crear la ficha: sin dólares no se cobra (si no, quedaría un prospecto sin su cobro).
    if (method === METODO_DOLARES && check && !check.free_available && !(dol.usdNum > 0)) { toast.error('Indica cuántos dólares te dio'); return; }
    setSaving(true);
    try {
      if (persona) { await cobrar(persona.id, persona.name); return; }
      const nota = force && dups?.length ? `Teléfono/nombre compartido con: ${dups.map((d) => d.name).join(', ')}` : null;
      const r = await createMember({
        firstname: nombre.trim(), lastname: apellido.trim() || undefined,
        phone_prefix: '52', phone_number: telDigits.slice(-10),
        force_duplicate: force || undefined, prospecto: true, notes: nota,
      });
      await cobrar(r.data.account_id, `${nombre.trim()} queda como prospecto`);
    } catch (e: any) {
      const err = e as ApiError;
      if (err?.status === 409 && Array.isArray(err.data?.duplicates)) setDups(err.data.duplicates as Dup[]);
      else toast.error(e?.message || 'No se pudo registrar la clase suelta');
    } finally { setSaving(false); }
  }

  const precio = check ? (check.free_available ? 'GRATIS (clase muestra)' : money(check.charge)) : '—';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl"
        data-testid="modal-clase-suelta">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><Ticket size={18} className="text-brand-blue" /> Clase suelta</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {!cajaAbierta && (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> No hay caja abierta: el cobro se registra, pero no entra a ninguna caja.
          </div>
        )}

        {persona ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm">
            <span className="flex items-center gap-2 text-content"><UserCheck size={15} className="text-emerald-600" /> {persona.name}</span>
            <button onClick={() => setPersona(null)} className="text-xs text-content-3 underline">cambiar</button>
          </div>
        ) : (
          <>
            <div className="mb-3 grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Nombre</label>
                <input autoFocus value={nombre} onChange={(e) => { setNombre(e.target.value); setDups(null); }} className={inputCls} data-testid="cs-nombre" />
              </div>
              <div>
                <label className={labelCls}>Apellido</label>
                <input value={apellido} onChange={(e) => { setApellido(e.target.value); setDups(null); }} className={inputCls} data-testid="cs-apellido" />
              </div>
            </div>
            <label className={labelCls}>Teléfono</label>
            <input inputMode="tel" value={tel} onChange={(e) => { setTel(e.target.value.replace(/[^\d\s-]/g, '')); setDups(null); }}
              placeholder="10 dígitos" className={`${inputCls} mb-3`} data-testid="cs-tel" />
          </>
        )}

        {dups && !persona && (
          <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="cs-coincidencias">
            <div className="mb-2 text-sm font-medium text-content">Ya hay alguien con ese teléfono o nombre:</div>
            <div className="space-y-1.5">
              {dups.map((d) => {
                const t = TIPO[d.tipo || ''] || TIPO.prospecto;
                return (
                  <div key={d.account_id} className="flex items-center justify-between gap-2 rounded-md bg-surface px-2.5 py-1.5 text-sm">
                    <div className="min-w-0">
                      <div className="truncate text-content">{d.name}</div>
                      <div className="flex items-center gap-1.5 text-xs text-content-3">
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${t.cls}`}>{t.label}</span>{d.phone || ''}
                      </div>
                    </div>
                    <button onClick={() => { setPersona({ id: d.account_id, name: d.name }); setDups(null); }}
                      className="shrink-0 rounded-md border border-line px-2 py-1 text-xs text-content-2 transition hover:bg-surface-2" data-testid="cs-es-ella">
                      Es esta persona
                    </button>
                  </div>
                );
              })}
            </div>
            <button onClick={() => submit(true)} disabled={saving}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-line px-2 py-1.5 text-xs text-content-2 transition hover:bg-surface-2"
              data-testid="cs-otra-persona">
              <UserPlus size={13} /> Es otra persona (p. ej. comparte el teléfono de mamá/papá)
            </button>
          </div>
        )}

        <label className={labelCls}>Clase</label>
        <select value={claseId ?? ''} onChange={(e) => setClaseId(e.target.value ? Number(e.target.value) : null)} className={`${inputCls} mb-3`} data-testid="cs-clase">
          <option value="">Elige la clase…</option>
          {clases.some((c) => c.hoy) && <optgroup label="Hoy">
            {clases.filter((c) => c.hoy).map((c) => <option key={c.class_id} value={c.class_id}>{c.hora} · {c.name}</option>)}
          </optgroup>}
          <optgroup label="Otras clases">
            {clases.filter((c) => !c.hoy).map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
          </optgroup>
        </select>

        <label className={labelCls}>Pago</label>
        <div className="mb-3 flex rounded-lg border border-line bg-surface p-0.5">
          {[...PAY_METHOD_OPTIONS, { v: METODO_DOLARES, label: 'Dólares' }].map((o) => (
            <button key={o.v} onClick={() => setMethod(o.v)}
              className={`flex-1 rounded-md px-2 py-1.5 text-sm font-medium transition ${method === o.v ? 'bg-accent text-on-accent' : 'text-content-2'}`}>{o.label}</button>
          ))}
        </div>

        {method === METODO_DOLARES && check && !check.free_available && (
          <DolaresMostrador total={check.charge} usd={dol.usd} setUsd={dol.setUsd} tc={dol.tc} />
        )}
        <div className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2">
          <span className="text-sm text-content-2">A cobrar</span>
          <span className="font-display text-lg font-bold text-content" data-testid="cs-precio">{precio}</span>
        </div>
        {!persona && <p className="mt-1.5 text-[11px] text-content-3">Si es alguien nuevo, queda guardado como <b>prospecto</b> (no como alumno).</p>}

        <div className="mt-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button disabled={!listo || saving || (!!dups && !persona)} onClick={() => submit(false)} data-testid="cs-cobrar"
            className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
            {saving ? 'Guardando…' : check?.free_available ? 'Registrar muestra' : `Cobrar ${check ? money(check.charge) : ''}`}
          </button>
        </div>
      </div>
    </div>, document.body);
}
